<template>
<div>
    <invoice-form :invoiceFormPropsFunctions="invoiceFormPropsFunctions" :getClientInvoiceList="getClientInvoiceList" ref="invoiceFormModal"></invoice-form>
    <div class="custom-search">
        <b-row class="align-items-end">
            <b-col>
                <label for="invoice-debit-no-input">{{ $t('invoice.debit_no') }}</label>
                <b-form-group>
                    <b-form-input id="invoice-debit-no-input" v-on:keyup.enter="getClientInvoiceList()" size="sm" v-model="debitNo" :placeholder="$t('invoice.debit_no')" type="text" class="d-inline-block" />
                </b-form-group>
            </b-col>
            <b-col>
                <label for="invoice-matter-no-input">{{ $t('invoice.matter_no') }}</label>
                <b-form-group>
                    <b-form-input id="invoice-matter-no-input" v-on:keyup.enter="getClientInvoiceList()" size="sm" v-model="lawMatterNo" :placeholder="$t('invoice.matter_no')" type="text" class="d-inline-block" />
                </b-form-group>
            </b-col>
            <b-col>
                <label for="invoice-employee-input">{{ $t('invoice.employee') }}</label>
                <b-form-group>
                    <b-form-select @change="getClientInvoiceList()" id="invoice-employee-input" size="sm" v-model="employeeSelect" value-field="id" text-field="name" :options="employeeOption" />
                </b-form-group>
            </b-col>
            <b-col>
                <label for="invoice-status-input">{{ $t('invoice.status') }}</label>
                <b-form-group>
                    <b-form-select @change="getClientInvoiceList()" id="invoice-status-input" size="sm" v-model="statusSelect" value-field="id" text-field="name" :options="statusOption" />
                </b-form-group>
            </b-col>
            <b-col>
                <label for="invoice-start-date-input">{{ $t('invoice.start_date') }}</label>
                <b-form-group>
                    <b-form-datepicker id="invoice-start-date-input" size="sm" v-model="startDate" reset-button today-button :label-today-button="$t('dateTime.today')" :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" :locale="$i18n.locale" :label-reset-button="$t('dateTime.reset')" :label-no-date-selected="$t('dateTime.select_date')" />
                </b-form-group>
            </b-col>
            <b-col>
                <label for="invoice-end-date-input">{{ $t('invoice.end_date') }}</label>
                <b-form-group>
                    <b-form-datepicker id="invoice-end-date-input" size="sm" v-model="endDate" reset-button today-button :label-today-button="$t('dateTime.today')" :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }" :locale="$i18n.locale" :label-reset-button="$t('dateTime.reset')" :label-no-date-selected="$t('dateTime.select_date')" />
                </b-form-group>
            </b-col>
            <b-col cols="auto">
                <b-form-group>
                    <b-button class="mr-1" size="sm" variant="outline-primary" @click="getClientInvoiceList()">{{ $t('invoice.list') }}</b-button>
                    <b-button class="mr-1" size="sm" variant="outline-primary" @click="exportExcel()">{{ $t('excel.excel') }}</b-button>
                    <b-button v-b-tooltip.html :title="$t('invoice.unpaid_invoices_excel')" size="sm" variant="outline-primary" @click="createClientUnpaidInvoicesExcelReport()">
                        <feather-icon icon="FileIcon" />
                    </b-button>
                </b-form-group>
            </b-col>
        </b-row>
    </div>
    <!-- table -->
    <vue-good-table id="clientInvoiceTable" ref="clientInvoiceTable" :columns="columns" :rows="rows" styleClass="vgt-table condensed tableSize" :select-options="{
        enabled: false,
        selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true, // disable the select info panel on top
        selectAllByGroup: true, // when used in combination with a grouped table, add a checkbox in the header row to check/uncheck the entire group
      }" :pagination-options="{
        enabled: true,
        perPage: pageLength
      }" theme="my-theme">
        <div slot="emptystate">
            {{ $t('dataTable.data_not_found') }}
        </div>
        <!-- STOCK-2979: filtre sonucuna göre Borçlu / Gönderilecek toplamları (para birimi başına satır).
             Slot her zaman verilir: vue-good-table slot varlığını reaktif olmayan bir computed ile cache'liyor,
             koşullu verilirse ilk boş yanıttan sonra toplam satırı hiç görünmez. -->
        <div slot="table-actions-bottom" class="client-invoice-total-wrap">
            <div v-if="statusTotalList.length > 0" class="client-invoice-total">
                <div v-for="statusTotal in statusTotalList" :key="statusTotal.lawInvoiceStatusId + '-' + statusTotal.currency">
                    {{ $t('invoice.total_row_label') }} — {{ totalStatusLabel(statusTotal.lawInvoiceStatusId) }}: {{ statusTotal.count }} {{ $t('invoice.invoice_count_suffix') }} ·
                    <span :class="{ 'invoice-age-overdue': statusTotal.lawInvoiceStatusId === unpaidStatusId }" :style="totalAmountStyle(statusTotal.lawInvoiceStatusId)">{{ $formatMoneyTr(statusTotal.total) }}</span>
                    {{ statusTotal.currency }}
                </div>
            </div>
        </div>
        <!-- Row: Table Row -->
        <template slot="table-column" slot-scope="props">
            <span v-if="props.column.field === 'debitNo'" class="text-nowrap">
                {{ $t('invoice.debit_no') }}
            </span>
            <span v-else-if="props.column.field === 'lawMatterRecordNo'" class="text-nowrap">
                {{ $t('invoice.matter_no') }}
            </span>
            <span v-else-if="props.column.field === 'matterOwnerText'" class="text-nowrap">
                {{ $t('invoice.matter_owner_short_name') }}
            </span>
            <span v-else-if="props.column.field === 'total'" class="text-nowrap">
                {{ $t('invoice.amount') }}
            </span>
            <span v-else-if="props.column.field === 'currency'" class="text-nowrap">
                {{ $t('invoice.currency_code') }}
            </span>
            <span v-else-if="props.column.field === 'date'" class="text-nowrap">
                {{ $t('invoice.invoice_date') }}
            </span>
            <span v-else-if="props.column.field === 'ageDays'" class="text-nowrap">
                {{ $t('invoice.age_days') }}
            </span>
            <span v-else-if="props.column.field === 'lawInvoiceStatus'" class="text-nowrap">
                {{ $t('invoice.status') }}
            </span>
            <span v-else-if="props.column.field === 'employee'" class="text-nowrap">
                {{ $t('invoice.employee') }}
            </span>
            <span v-else-if="props.column.field === 'process'" class="text-nowrap"></span>
        </template>
        <!-- Column: Table Column -->
        <template slot="table-row" slot-scope="props">
            <!-- Column: Process -->
            <span v-if="props.column.field === 'date'">
                {{ $formatFn(props.row.date) }}
            </span>
            <span v-else-if="props.column.field === 'total'">
                {{ $formatMoneyTr(props.row.total) }}
            </span>
            <span v-else-if="props.column.field === 'matterOwnerText'">
                <div v-b-tooltip.hover="{ title: props.row.matterOwnerTitle, customClass: 'wh-tooltip' }" class="wh-clamp-2">{{ props.row.matterOwnerText }}</div>
            </span>
            <span v-else-if="props.column.field === 'ageDays'">
                <span v-if="props.row.ageDays === null" class="invoice-age-empty">—</span>
                <span v-else :class="{ 'invoice-age-overdue': props.row.ageDays > 90 }">{{ props.row.ageDays }}</span>
            </span>
            <span v-else-if="props.column.field === 'lawInvoiceStatus'">
                <b-badge :style="$badgeStyle(invoiceStatusKind(props.row.lawInvoiceStatus))">
                    {{ translateInvoiceStatus(props.row.lawInvoiceStatus) }}
                </b-badge>
            </span>

            <span v-else-if="props.column.field === 'process'">
                <!-- STOCK 1420: delete disabled while lawInvoiceStatusId == 1 rule is reintroduced; single action for now -->
                <list-actions-dropdown :actions="[
                    { icon: 'EditIcon', label: $t('others.edit'), handler: () => getInfo(props.row.id) }
                ]" />
            </span>
        </template>
        <!-- pagination -->
        <template slot="pagination-bottom" slot-scope="props">
            <div class="d-flex justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-0 mt-1">
                    <span class="text-nowrap"> {{ $t('dataTable.total') }} {{ total }} {{ $t('dataTable.record') }}</span>
                    <b-form-select size="sm" v-model="pageLength" :options="['10','25','50','100']" class="mx-1" @input="(value)=>props.perPageChanged({currentPerPage:value})" />
                    <span class="text-nowrap"> {{ $t('dataTable.row_is_show') }} </span>
                </div>
                <div>
                    <b-pagination :value="1" :total-rows="total" v-model="pageNo" :per-page="pageLength" first-number last-number align="right" prev-class="prev-item" next-class="next-item" class="mt-1 mb-0" @input="(value) => getClientInvoiceList()">
                        <template #prev-text>
                            <feather-icon icon="ChevronLeftIcon" size="18" />
                        </template>
                        <template #next-text>
                            <feather-icon icon="ChevronRightIcon" size="18" />
                        </template>
                    </b-pagination>
                </div>
            </div>
        </template>
    </vue-good-table>
</div>
</template>

<script>
// LawInvoiceStatus tablosu ID'leri (STOCK-2979, DB'de doğrulandı)
const INVOICE_STATUS = { ToBeSent: 1, Cancelled: 2, Unpaid: 3, Paid: 4 }
const DAY_MS = 24 * 60 * 60 * 1000

export default {
    props: {
        lawClientId: {
            type: Number
        }
    },
    data() {
        return {
            total: 0,
            pageLength: 50,
            invoiceFormPropsFunctions: 4,
            dir: false,
            pageNo: 1,
            columns: [{
                    label: this.$t('invoice.debit_no'),
                    field: `debitNo`,
                },
                {
                    label: this.$t('invoice.matter_no'),
                    field: `lawMatterRecordNo`,
                },
                {
                    label: this.$t('invoice.matter_owner_short_name'),
                    field: `matterOwnerText`,
                },
                {
                    label: this.$t('invoice.amount'),
                    field: `total`,
                    tdClass: 'text-right',
                    thClass: 'text-right',
                },
                {
                    label: this.$t('invoice.currency_code'),
                    field: `currency`,
                },
                {
                    label: this.$t('invoice.invoice_date'),
                    field: `date`,
                },
                {
                    label: this.$t('invoice.age_days'),
                    field: `ageDays`,
                    type: 'number',
                    tdClass: 'text-right',
                    thClass: 'text-right',
                },
                {
                    label: this.$t('invoice.status'),
                    field: `lawInvoiceStatus`,
                },
                {
                    label: this.$t('invoice.employee'),
                    field: `employee`,
                },
                {
                    label: '',
                    field: 'process',
                    width: '6em',
                    sortable: false
                },
            ],
            rows: [],
            searchTerm: '',
            debitNo: '',
            lawMatterNo: '',
            employeeSelect: 0,
            employeeOption: [],
            statusSelect: 0,
            statusOption: [],
            paid: true,
            notPaid: false,
            count: '',
            searchDebounce: null,
            requestSeq: 0,
            startDate: null,
            endDate: null,
            statusTotalList: []
        }
    },
    computed: {
        unpaidStatusId() {
            return INVOICE_STATUS.Unpaid
        }
    },
    mounted() {
        this.getClientInvoiceList()
    },
    watch: {
        debitNo() {
            this.debouncedSearch()
        },
        lawMatterNo() {
            this.debouncedSearch()
        },
        startDate() {
            this.debouncedSearch()
        },
        endDate() {
            this.debouncedSearch()
        }
    },
    methods: {
        // Yalnız Borçlu faturada gün sayılır; Gönderilecek / Ödendi / İptal için null ("—")
        invoiceAgeDays(row) {
            if (row.lawInvoiceStatusId !== INVOICE_STATUS.Unpaid || !row.date) return null
            const invoiceDate = new Date(row.date)
            const now = new Date()
            const start = Date.UTC(invoiceDate.getFullYear(), invoiceDate.getMonth(), invoiceDate.getDate())
            const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
            return Math.round((today - start) / DAY_MS)
        },
        matterOwnerLabel(matter) {
            return [matter.owner, matter.shortName].filter(Boolean).join(' · ')
        },
        // Hücre: ilk dosyanın sahibi · kısa adı +{kalan dosya}; tooltip: tüm dosyalar
        prepareInvoiceRow(row) {
            const matterList = row.lawMatterOwnerList || []
            let matterOwnerText = ''
            if (matterList.length > 0) {
                matterOwnerText = this.matterOwnerLabel(matterList[0])
                if (matterList.length > 1) matterOwnerText += ` +${matterList.length - 1}`
            }
            // Dosya No hücresi de aynı sırada: backend'in birleştirdiği metin sırasız, "ilk dosya" ile çelişmesin
            const lawMatterRecordNo = matterList.length > 0 ? matterList.map(m => m.recordNo).join(' ') : row.lawMatterRecordNo
            return Object.assign({}, row, {
                lawMatterRecordNo: lawMatterRecordNo,
                matterOwnerText: matterOwnerText,
                matterOwnerTitle: matterList.map(this.matterOwnerLabel).filter(Boolean).join(', '),
                ageDays: this.invoiceAgeDays(row)
            })
        },
        // Gönderilecek tutarı tablodaki Gönderilecek rozetiyle aynı renkte (rozet kaynağından okunur)
        totalAmountStyle(statusId) {
            return statusId === INVOICE_STATUS.ToBeSent ? { color: this.$badgeStyle('info').color } : null
        },
        totalStatusLabel(statusId) {
            return statusId === INVOICE_STATUS.Unpaid ? this.$t('invoice.status_unpaid') : this.$t('invoice.status_tobesent')
        },
        debouncedSearch() {
            clearTimeout(this.searchDebounce)
            this.searchDebounce = setTimeout(() => {
                if (this.pageNo !== undefined) this.pageNo = 1
                this.getClientInvoiceList()
            }, 300)
        },
        async deleted(data) {
            this.count = data.lawInvoiceDetailCount
            data = {
                id: data.id
            }
            const controller = {
                name: 'LawInvoice'
            }
            const payload = {
                data: data,
                controller: controller
            }
            this.$DeleteInvoiceConfirm(this.count).then(result => {
                if (result.value) {
                    this.$store.dispatch('moduleAuth/delete', payload).then((response) => {
                        if (response.data.resultStatus == true) {
                            this.$DeleteAlert()
                            this.getClientInvoiceList()
                        } else {
                            if (response.data.resultMessage == 'ThereIsAInvoiceDetailInThisRecord') {
                                this.$InvoiceDetailError()
                            } else {
                                this.$CustomWarningAlert(response.data.resultMessage)
                            }
                            this.getClientInvoiceList()
                        }
                    })
                }
            })
        },
        async getInfo(id) {
            this.$refs.invoiceFormModal.showInfo(id)
        },
        translateInvoiceStatus(name) {
            const map = {
                ToBeSent: this.$t('invoice.status_tobesent'),
                Unpaid: this.$t('invoice.status_unpaid'),
                Paid: this.$t('invoice.status_paid'),
                Cancelled: this.$t('invoice.status_cancelled'),
                Canceled: this.$t('invoice.status_cancelled')
            }
            return map[name] || name
        },
        invoiceStatusKind(name) {
            const map = { Paid: 'success', Unpaid: 'danger', ToBeSent: 'info', Cancelled: 'warning', Canceled: 'warning' }
            return map[name] || 'neutral'
        },
        getClientInvoiceList() {
            const requestSeq = ++this.requestSeq
            const controller = {
                name: 'LawInvoice',
                actionName: 'GetLawInvoiceListByClient'
            }
            const data = {
                pageLength: this.pageLength,
                pageNo: this.pageNo,
                debitNo: this.debitNo,
                lawMatterNo: this.lawMatterNo,
                lawInvoiceTypeId: 0,
                employeeId: this.employeeSelect,
                lawInvoiceStatusId: this.statusSelect,
                lawClientId: this.lawClientId,
                startDate: this.startDate || null,
                endDate: this.endDate || null
            }
            const payload = {
                data: data,
                controller: controller
            }
            this.$store.dispatch('moduleAuth/customService', payload).then((response) => {
                // Daha yeni bir istek gonderildiyse bu yanit bayattir, uygulanmaz
                if (requestSeq !== this.requestSeq) return
                if (response.data.resultStatus == true) {
                    this.total = response.data.total
                    this.employeeOption = response.data.employeeList
                    // Ana Faturalar ekranıyla aynı: durum adları Türkçe gösterilir
                    this.statusOption = response.data.lawInvoiceStatusList.map(s => ({ id: s.id, name: this.translateInvoiceStatus(s.name) }))
                    this.rows = response.data.lawInvoiceList.map(this.prepareInvoiceRow)
                    this.statusTotalList = response.data.statusTotalList || []
                    this.statusOption.splice(0, 0, this.$nullSelected()),
                        this.employeeOption.splice(0, 0, this.$nullSelected())
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                }
            })
        },
        exportExcel() {
            const controller = {
                name: 'LawInvoice',
                actionName: 'CanExport'
            }
            const data = {
                columnList: this.columns,
                DataList: this.rows
            }
            const payload = {
                data: data,
                controller: controller
            }
            this.$store.dispatch('moduleAuth/customService', payload).then((response) => {
                if (response.data.resultStatus == true) {
                    this.$downloadFile("Invoice", response.data.base64, 'xlsx')
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                }
            })
        },
        createClientUnpaidInvoicesExcelReport() {
            const controller = {
                name: 'LawInvoice',
                actionName: 'CreateLawClientUnpaidInvoiceExcelReport'
            }
            const data = {
                id: this.lawClientId
            }
            const payload = {
                data: data,
                controller: controller
            }
            this.$store.dispatch('moduleAuth/customService', payload).then((response) => {
                if (response.data.resultStatus == true) {
                    this.$downloadFile(response.data.fileName, response.data.base64, 'xlsx')
                } else {
                    this.$WarningAlert(response.data.resultMessage)
                }
            })
        }
    },
}
</script>

<style lang="scss">
.excel-css {
    float: right;
    margin-right: 1em;
    width: 9.5em;
    color: #7367f0;
    border: 1px solid #7367f0;
    background-color: transparent;
    margin-left: 1em;
    height: 2em;
    border-radius: 0.358rem;
}

.tableSize {
    font-size: 13px !important;
    border-collapse: collapse;
    width: 100%;
    max-width: 100%;
    table-layout: auto;
}

.datepickerStyle {
    max-height: 2.2em;
    font-family: fangsong;
}

/* STOCK-2979 */
.vgt-wrap__actions-footer:has(> .client-invoice-total-wrap) {
    border: 0;
}

.client-invoice-total {
    background-color: #F9FAFB;
    border: 1px solid #dcdfe6;
    border-top: 2px solid #E5E7EB;
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
}

.invoice-age-empty {
    color: #9CA3AF;
}

.invoice-age-overdue {
    color: #B91C1C;
    font-weight: 600;
}
</style>
